import { AnalyticsService } from '@alison/analytics/client';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { METRIC_OPERATORS } from '../components/filters/constants';
import {
  type AnyFilterKey,
  FILTER_CATALOG,
  filterDefinition,
  isCustomFilterKey,
} from '../components/filters/filterCatalog';
import type { MetricCondition } from '../components/filters/types';
import { commaSeparatedArray } from './search-codec';

const FILTER_KEYS = FILTER_CATALOG.map((definition) => definition.key);

const metricSchema = z
  .object({
    conditions: z.array(
      z.object({
        field: z.string(),
        operator: z.enum(METRIC_OPERATORS),
        value: z.string(),
      }),
    ),
  })
  .nullable()
  .catch(null);

export type MetricState = z.infer<typeof metricSchema>;
export type AddedFilter = { key: AnyFilterKey; values: string[] };
export type FilterState = { added: AddedFilter[]; metric: MetricState };
export type FilterSearchRoute = '/workflows' | '/explore' | '/elements-explorer' | '.';
export type FilterFieldsSearch = Partial<Record<AnyFilterKey, string[]>>;

export const createDefaultCondition = (): MetricCondition => ({
  field: '',
  operator: '<',
  value: '',
});

function dropOrphans(added: AddedFilter[]): AddedFilter[] {
  const presentKeys = new Set(added.map((filter) => filter.key));
  return added.filter((filter) => {
    const dependsOn = filterDefinition(filter.key).dependsOn;
    return dependsOn === undefined || presentKeys.has(dependsOn);
  });
}

function withoutFilterAndDependents(added: AddedFilter[], key: AnyFilterKey): AddedFilter[] {
  return dropOrphans(added.filter((filter) => filter.key !== key));
}

export function filtersToScope(added: AddedFilter[]): Record<string, string[]> | undefined {
  const out = Object.fromEntries(
    added.filter((filter) => filter.values.length > 0).map((filter) => [filter.key, filter.values]),
  );
  return Object.keys(out).length ? out : undefined;
}

/** The filter keys a search carries: the catalog's, then any tenant-declared ones. */
export function searchFilterKeys(search: Record<string, unknown>): AnyFilterKey[] {
  return [...FILTER_KEYS, ...Object.keys(search).filter(isCustomFilterKey)];
}

export function filterStateFromSearch(search: Record<string, unknown>): FilterState {
  const added: AddedFilter[] = [];
  for (const key of searchFilterKeys(search)) {
    const raw = search[key];
    if (raw === undefined) continue;
    added.push({ key, values: commaSeparatedArray.catch([]).parse(raw) });
  }
  return { added: dropOrphans(added), metric: metricSchema.parse(search['metric']) };
}

export function validateFilterSearch(
  search: Record<string, unknown>,
): FilterFieldsSearch & { metric?: MetricState } {
  const { added, metric } = filterStateFromSearch(search);
  return {
    ...Object.fromEntries(added.map((filter) => [filter.key, filter.values])),
    ...(metric ? { metric } : {}),
  };
}

function searchFromFilterState(
  previous: Record<string, unknown>,
  state: FilterState,
): Record<string, unknown> {
  const base = { ...previous };
  for (const key of searchFilterKeys(previous)) delete base[key];
  for (const filter of state.added) base[filter.key] = filter.values;
  if (state.metric) base['metric'] = state.metric;
  else delete base['metric'];
  return base;
}

export function useFilterSearch(to: FilterSearchRoute = '.') {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const { added, metric } = filterStateFromSearch(search);

  const setFilterState = (next: FilterState): void => {
    void navigate({
      to,
      replace: true,
      search: (previous) => searchFromFilterState(previous as Record<string, unknown>, next),
    });
  };

  return {
    added,
    metric,
    addFilter: (key: AnyFilterKey): void => {
      AnalyticsService.track('Filter - Added', { filter: key });
      setFilterState({ added: [...added, { key, values: [] }], metric });
    },
    removeFilter: (key: AnyFilterKey): void => {
      AnalyticsService.track('Filter - Removed', { filter: key });
      setFilterState({ added: withoutFilterAndDependents(added, key), metric });
    },
    patchFilter: (key: AnyFilterKey, values: string[]): void =>
      setFilterState({
        added: added.some((filter) => filter.key === key)
          ? added.map((filter) => (filter.key === key ? { ...filter, values } : filter))
          : [...added, { key, values }],
        metric,
      }),
    addMetric: (): void => {
      AnalyticsService.track('Filter - Metric Added');
      setFilterState({ added, metric: { conditions: [createDefaultCondition()] } });
    },
    setMetricConditions: (conditions: MetricCondition[]): void =>
      setFilterState({ added, metric: { conditions } }),
    removeMetric: (): void => {
      AnalyticsService.track('Filter - Metric Removed');
      setFilterState({ added, metric: null });
    },
  };
}
